"use client";

import React, { useEffect, useRef } from "react";

export default function ContactSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".reveal-hidden").forEach((el) => {
              el.classList.add("revealed");
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  const contactDetails = [
    {
      icon: "📍",
      label: "Address",
      value: "Adde Vishwanathapura Road, Rajanukunte, Bengaluru, Karnataka 560064",
      href: "https://maps.google.com/?q=Rajanukunte+Bengaluru+Karnataka+560064",
      linkLabel: "Get Directions",
    },
    {
      icon: "📞",
      label: "Phone",
      value: "+91 98490 07187",
      href: "tel:+919849007187",
      linkLabel: "Call Now",
    },
    {
      icon: "✉️",
      label: "Email",
      value: "varahistudios2024@gmail.com",
      href: "mailto:varahistudios2024@gmail.com",
      linkLabel: "Send Email",
    },
    {
      icon: "🕐",
      label: "Hours",
      value: "Mon – Sat: 9:00 AM – 8:00 PM\nSunday: 10:00 AM – 2:00 PM",
      href: null,
      linkLabel: null,
    },
  ];

  return (
    <section
      id="contact"
      ref={sectionRef}
      className="py-24 md:py-32 relative"
      style={{ background: "#0e0e0e" }}
    >
      <div className="gold-divider absolute top-0 left-0 right-0" style={{ zIndex: 1 }} />
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16 reveal-hidden">
          <span className="badge-yellow px-3 py-1 rounded-full text-xs font-display font-bold uppercase tracking-widest">
            Find Us
          </span>
          <h2
            className="font-display font-black mt-4 leading-tight tracking-tight"
            style={{ fontSize: "clamp(2rem, 4vw, 3rem)" }}
          >
            Come Visit the{" "}
            <span className="text-yellow">Studio</span>
          </h2>
          <p
            className="mt-3 text-base max-w-lg mx-auto"
            style={{ color: "rgba(255,255,255,0.5)" }}
          >
            We're located in Rajanukunte, North Bengaluru — easy to reach from
            Yelahanka, Hebbal, and Devanahalli.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Left: Contact Details */}
          <div className="space-y-5 reveal-hidden">
            {contactDetails?.map((c, i) => (
              <div
                key={c?.label}
                className={`reveal-hidden stagger-${i + 1} stat-card rounded-2xl p-6 flex gap-5 items-start`}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
                  style={{ background: "rgba(242,201,76,0.1)", border: "1px solid rgba(242,201,76,0.2)" }}
                >
                  {c?.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <p
                    className="text-xs font-display font-bold uppercase tracking-widest mb-1"
                    style={{ color: "rgba(255,255,255,0.4)" }}
                  >
                    {c?.label}
                  </p>
                  <p
                    className="text-sm leading-relaxed whitespace-pre-line"
                    style={{ color: "rgba(255,255,255,0.75)" }}
                  >
                    {c?.value}
                  </p>
                  {c?.href && c?.linkLabel && (
                    <a
                      href={c?.href}
                      target={c?.href?.startsWith("http") ? "_blank" : undefined}
                      rel={c?.href?.startsWith("http") ? "noopener noreferrer" : undefined}
                      className="contact-link inline-flex items-center gap-1 text-xs font-display font-bold mt-2"
                      style={{ color: "#F2C94C" }}
                    >
                      {c?.linkLabel} →
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Right: Map Embed */}
          <div className="reveal-hidden stagger-2">
            <div
              className="rounded-3xl overflow-hidden"
              style={{
                height: "420px",
                border: "1px solid rgba(255,255,255,0.08)",
                position: "relative",
              }}
            >
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3886.0!2d77.5946!3d13.1390!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTPCsDA4JzIwLjQiTiA3N8KwMzUnNDAuNiJF!5e0!3m2!1sen!2sin!4v1677000000000!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0, filter: "invert(90%) hue-rotate(180deg)" }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Hi-Hats Rajanukunte location map on Adde Vishwanathapura Road Bengaluru"
              />
            </div>
            {/* Quick CTA below map */}
            <div
              className="mt-4 rounded-2xl p-5 flex items-center justify-between gap-4"
              style={{
                background: "rgba(242,201,76,0.06)",
                border: "1px solid rgba(242,201,76,0.15)",
              }}
            >
              <div>
                <p className="font-display font-bold text-sm text-yellow">Ready to visit?</p>
                <p className="text-xs mt-0.5" style={{ color: "rgba(255,255,255,0.5)" }}>
                  Drop by anytime during working hours — no appointment needed for a studio tour.
                </p>
              </div>
              <a
                href="tel:+919849007187"
                className="btn-yellow flex-shrink-0 px-5 py-3 rounded-xl text-xs font-display font-black whitespace-nowrap"
              >
                Call Us
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}